import { useMemo, useState } from 'react';
import type { ICarReport } from '../model/carReport.types';
import { toDisplayStatus } from '../config/carReportStatus';

export const ALL_VALUE = 'ALL';
export type Option = { text: string; value: string };

export const extractOriginDest = (ufi: string) => {
    if (!ufi) return { origin: '', dest: '' };
    const m = ufi.match(/([A-Z]{4})([A-Z]{4})$/);
    return m ? { origin: m[1], dest: m[2] } : { origin: '', dest: '' };
};

const norm = (v?: string) => String(v ?? '').trim();
const normUpper = (v?: string) => norm(v).toUpperCase();

// dropdown options are built from backend fixed full arrays (not dynamic)
const buildOptionsFromApi = (values: (string | undefined)[]) => {
    const normalized = values.map(norm).filter((v) => v.length > 0);
    const uniq = Array.from(new Set(normalized));
    return [{ text: 'ALL', value: ALL_VALUE }, ...uniq.map((v) => ({ text: v, value: v }))];
};

const buildStatusOptionsFromApi = (values: (string | undefined)[]) => {
    const normalized = values.map(norm).filter((v) => v.length > 0);
    const uniq = Array.from(new Set(normalized));

    const mapped = uniq.map((code) => ({
        value: code,
        text: toDisplayStatus(code),
    }));

    return [{ text: 'ALL', value: ALL_VALUE }, ...mapped];
};

type FilterState = {
    origin: string;
    dest: string;
    category: string;
    subject: string;
    creator: string;
    assigneeLead: string;
    assigneeGroup: string;
    status: string;
};

export type CarFiltersInit = Partial<FilterState>;

// Fixed full options from backend
export type FixedFilterOptionsFromApi = Partial<{
    origin: string[];
    dest: string[];
    category: string[];
    subject: string[];
    creator: string[];
    assigneeLead: string[];
    assigneeGroup: string[];
    status: string[];
}>;

export function useCarSearchAndFilter(
    list: ICarReport[],
    init?: CarFiltersInit,
    fixedOptionsFromApi?: FixedFilterOptionsFromApi,
) {
    const [origin, setOrigin] = useState(() => init?.origin ?? '');
    const [dest, setDest] = useState(() => init?.dest ?? '');
    const [category, setCategory] = useState(() => init?.category ?? '');
    const [subject, setSubject] = useState(() => init?.subject ?? '');
    const [creator, setCreator] = useState(() => init?.creator ?? '');
    const [assigneeLead, setAssigneeLead] = useState(() => init?.assigneeLead ?? '');
    const [assigneeGroup, setAssigneeGroup] = useState(() => init?.assigneeGroup ?? '');
    const [status, setStatus] = useState(() => init?.status ?? '');

    // Filtering is server-side now; no more local filtering/collapsing options
    const filteredList = useMemo(() => list ?? [], [list]);

    const options = useMemo(() => {
        // options always come from fixedOptionsFromApi (full set)
        const src = fixedOptionsFromApi ?? {};

        return {
            origin: buildOptionsFromApi(src.origin ?? []),
            dest: buildOptionsFromApi(src.dest ?? []),
            category: buildOptionsFromApi(src.category ?? []),
            subject: buildOptionsFromApi(src.subject ?? []),
            creator: buildOptionsFromApi(src.creator ?? []),
            assigneeLead: buildOptionsFromApi(src.assigneeLead ?? []),
            assigneeGroup: buildOptionsFromApi(src.assigneeGroup ?? []),

            status: buildStatusOptionsFromApi(src.status ?? []),
        };
    }, [fixedOptionsFromApi]);

    const asAllOrValue = (v: string) => (v === ALL_VALUE ? '' : v);

    return {
        origin,
        setOrigin,
        dest,
        setDest,
        category,
        setCategory,
        subject,
        setSubject,
        creator,
        setCreator,
        assigneeLead,
        setAssigneeLead,
        assigneeGroup,
        setAssigneeGroup,
        status,
        setStatus,

        filteredList,
        options,
        asAllOrValue,
    };
}
